import { connect }                   from 'react-redux';
import { withRouter }                from 'react-router';
import * as PropTypes                from 'prop-types';
import {
    compose,
    withState,
    withHandlers,
    getContext,
    renderComponent,
    branch,
    lifecycle
}                                    from 'recompose';
import { mapCustomFields }           from '../CustomFields/CustomFieldsActions';
import { capitalizeFirstLetter }     from '../../helpers/stringUtils';
import MessageTemplatesQuery         from './MessageTemplatesQuery';
import LoadingAnimation              from '../../components/LoadingAnimation';
import ErrorMessage                  from '../../components/ErrorMessage';
// components
import MessageTemplatesListComponent from './MessageTemplatesListComponent';
import { graphql, DataValue }        from 'react-apollo';
import { getQueryStringValueByKey }  from '../../helpers/queryStringUtils';
import {
    MessageTemplatesList as MessageTemplatesListType,
    MessageTemplatesContainerProps,
    MessageTemplatesPayload,
    MessageTemplatesVariables
}                                    from './MessageTemplatesListTypes';
import { getFilteredTemplates }      from './MessageTemplatesListSelector';
import { haveMessageTemplate }       from '../../helpers/messenger';
import cache, {
    HELP_CARDS_KEY,
    FIVE_YEARS
}                                    from '../../services/CacheService';
import { getSession } from '../../services/SessionService';
import { UserSession } from '../../types';
import { getNoobieStatus } from '../../services/NoobieService';

const FIRST_PAGE_INDEX = 1;
const DEFAULT_ITEMS_PER_PAGE = 3;

/* Redux */
const mapStateToProps = (store, props: MessageTemplatesContainerProps): Partial<MessageTemplatesContainerProps> => ({
    customFields: store.customFieldsStore
});

const mapDispatchers = (dispatch, ownProps: MessageTemplatesContainerProps) => ({
    mapCustomFields(messageTemplates: MessageTemplatesListType) {
        const dynamicFieldInfo = createDynamicFieldInfo(ownProps.session, ownProps.recipient);

        dispatch(mapCustomFields(messageTemplates, dynamicFieldInfo));
    }
});

const createDynamicFieldInfo = (session, recipient) => ({
    customerName: capitalizeFirstLetter(recipient.name ? recipient.name.trim().split(' ')[0] : null),
    salesRepName: capitalizeFirstLetter(session.firstname),
    dealership: recipient.outlet_locator || recipient.dealership_name,
});

/* GraphQL */
// TODO: one day this could be reusable - graphql utils
const renderLoading = (component, propName = 'data') => (
    branch(
        (props: MessageTemplatesContainerProps) => props[propName] && props[propName].loading,
        renderComponent(component)
    )
);

const renderError = (component, propName = 'data') => (
    branch(
        (props: MessageTemplatesContainerProps) => props[propName] && props[propName].error,
        renderComponent(component)
    )
);

const mapTemplates = lifecycle<MessageTemplatesContainerProps, {}>({
    componentWillMount() {
        this.props.mapCustomFields(this.props.data.messageTemplates.templates);
    },
    async componentDidMount() {
        const session: UserSession = await getSession();
        const { details: { conversationNoobieUser: isNoobie }} = await getNoobieStatus(session);
        const hasSeenCards = await cache.get(HELP_CARDS_KEY);

        if (!hasSeenCards && isNoobie) {
            this.props.setShowCards(true);
            cache.add(HELP_CARDS_KEY, new Date(), FIVE_YEARS);
        }
    },
    componentWillUpdate(nextProps: MessageTemplatesContainerProps) {
        if (this.props.data.messageTemplates !== nextProps.data.messageTemplates) { // FIXME: is this a sufficent equality check?
            this.props.mapCustomFields(nextProps.data.messageTemplates.templates);
        }
    }
});

const watchTemplates = lifecycle<MessageTemplatesContainerProps, {}>({
    componentWillReceiveProps({ setHaveMessageTemplates, data }) {
        setHaveMessageTemplates && setHaveMessageTemplates(haveMessageTemplate(data.messageTemplates));
    }
});

const getCriteria = (props: MessageTemplatesContainerProps) => ({
    variables: {
        criteria: {
            callListId: getQueryStringValueByKey('calllist_id') && parseInt(getQueryStringValueByKey('calllist_id'), 10),
            conversationId: props.conversationId,
            page: {
                pageNumber: FIRST_PAGE_INDEX,
                quantityRecords: props.itemsPerPage || DEFAULT_ITEMS_PER_PAGE
            }
        }
    }
});

enum FetchModes {
    NEXT = 'next',
    PREVIOUS = 'previous'
}

type FetchMode = FetchModes.NEXT | FetchModes.PREVIOUS;

const updateCriteria = ({ variables, messageTemplates }: DataValue<MessageTemplatesPayload, MessageTemplatesVariables>, flag: FetchMode) => ({
    ...variables,
    criteria: {
        ...variables.criteria,
        page: {
            ...variables.criteria.page,
            pageNumber: flag === FetchModes.NEXT ? messageTemplates.pageInfo.current + 1 : messageTemplates.pageInfo.current - 1
        }
    }
});


const MessageTemplatesContainer = compose(
    withRouter,
    getContext({
        session: PropTypes.any.isRequired
    }),
    graphql(MessageTemplatesQuery, {
        options: getCriteria,
        props({ data, ownProps }) {
            return {
                data: getFilteredTemplates(data, ownProps),
                loadMoreData() {
                    data.fetchMore({
                        variables: updateCriteria(data, FetchModes.NEXT),
                        updateQuery: (previousResult, { fetchMoreResult }) => ({
                            ...previousResult,
                            messageTemplates: {
                                ...previousResult.messageTemplates,
                                templates: [...previousResult.messageTemplates.templates, ...fetchMoreResult.messageTemplates.templates],
                                pageInfo: {
                                    ...previousResult.messageTemplates.pageInfo,
                                    ...fetchMoreResult.messageTemplates.pageInfo
                                }
                            }
                        })
                    });
                },
                loadPrevPage() {
                    data.fetchMore({
                        variables: updateCriteria(data, FetchModes.PREVIOUS),
                        updateQuery: (previousResult, { fetchMoreResult }) => fetchMoreResult
                    });
                },
                loadNextPage() {
                    data.fetchMore({
                        variables: updateCriteria(data, FetchModes.NEXT),
                        updateQuery: (previousResult, { fetchMoreResult }) => fetchMoreResult
                    });
                }
            };
        }
    }),
    renderLoading(LoadingAnimation),
    renderError(ErrorMessage),
    connect(mapStateToProps, mapDispatchers),
    withState('activeMessageTemplate', 'setMessageTemplateState', null),
    withState('showCards', 'setShowCards', false),
    withHandlers({
        selectMessageTemplate: ({ activeMessageTemplate, setMessageTemplateState }) => (templateId?: string) => {
            if (activeMessageTemplate && templateId) {
                return false;
            }

            setMessageTemplateState(templateId);
        }
    }),
    mapTemplates,
    watchTemplates,
    // FIXME: investigate performance hit of second branch condition
    // for now, we need the first branch to ensure that data is loaded before we map message templates
    branch(({ customFields }) => !customFields.customFieldsLoaded, renderComponent(LoadingAnimation))
);

export default MessageTemplatesContainer(MessageTemplatesListComponent) as React.ComponentClass<Partial<MessageTemplatesContainerProps>>;



// WEBPACK FOOTER //
// ./src/sections/MessageTemplatesList/index.ts